/*
 * Lirago Yard V3.1 gallery state engine.
 *
 * Shopify supplies product/variant/cart data. This module owns the active
 * media target for the rendered gallery and deliberately ignores
 * variant.featured_media when a media_manifest is present.
 */
(() => {
  const ROLE_ORDER = [
    'hero',
    'clean',
    'size_compare',
    'human_scale',
    'lifestyle_2',
    'lifestyle_3',
    'included',
    'detail_material',
    'color_overview'
  ];

  const COVER_ROLES = new Set(['hero', 'lifestyle_2', 'lifestyle_3']);

  function parseJson(node, fallback) {
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      return fallback;
    }
  }

  function normalizeMediaId(value) {
    if (value === null || value === undefined || value === '') return '';
    return String(value).split('/').pop();
  }

  function normalizeSize(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/×/g, 'x')
      .replace(/\s/g, '')
      .replace(/"/g, '')
      .replace(/w/g, '')
      .replace(/h/g, '');
  }

  class LiragoGalleryState {
    constructor(root, host) {
      this.root = root;
      this.host = host;
      this.product = parseJson(host.querySelector('[data-product-json]'), null);
      this.manifest = parseJson(host.querySelector('[data-media-manifest]'), null);
      this.manifestActive = Boolean(this.manifest && typeof this.manifest === 'object');
      this.bambooUx = host.dataset.bambooUx === 'true';
      if (this.bambooUx) this.manifestActive = false;
      this.bambooSequence = null;
      this.mediaRoles = new Map();
      this.activeMediaId = '';
      this.internalSync = false;
      this.pilot = root.hasAttribute('data-media-pilot');
      this.showSecondary = false;
      this.touchStart = null;
      this.viewerIndex = 0;
      this.viewerSlides = [];

      this.indexManifestRoles();
      this.refreshSlides();
      this.bindEvents();
      this.applyRoleFit();
      this.syncControls(this.currentIndex());
      const initialVariant = this.currentVariant();
      if (initialVariant) this.applyVariant(initialVariant, null, true);

      // The variant event path owns gallery refreshes; the current theme keeps
      // the gallery subtree static, so no broad DOM observer is needed.
    }

    refreshSlides() {
      this.slides = [...this.root.querySelectorAll('.product-gallery__slide[data-media-id]')];
      this.thumbs = [...this.root.querySelectorAll('[data-gallery-thumb]')];
      this.navigationSlides = this.bambooUx && this.bambooSequence
        ? this.bambooSequence.map((id) => this.slides.find((slide) => normalizeMediaId(slide.dataset.mediaId) === id)).filter(Boolean)
        : this.pilot && !this.showSecondary
          ? this.slides.filter((slide) => slide.dataset.galleryPrimary === 'true')
          : this.slides;
    }

    indexManifestRoles() {
      if (!this.manifestActive) return;

      const record = (role, reference, context = {}) => {
        if (!reference) return;
        const rawId = reference.media_gid || reference.mediaId || reference.id || reference;
        const mediaId = normalizeMediaId(rawId);
        if (!mediaId) return;
        const existing = this.mediaRoles.get(mediaId) || { roles: [], contexts: [] };
        if (!existing.roles.includes(role)) existing.roles.push(role);
        existing.contexts.push(context);
        this.mediaRoles.set(mediaId, existing);
      };

      Object.entries(this.manifest.shared || {}).forEach(([role, reference]) => {
        record(role, reference, { shared: true });
      });

      Object.entries(this.manifest.colors || {}).forEach(([color, roles]) => {
        Object.entries(roles || {}).forEach(([role, reference]) => {
          record(role, reference, { color });
        });
      });

      Object.entries(this.manifest.sizes || {}).forEach(([size, roles]) => {
        Object.entries(roles || {}).forEach(([role, reference]) => {
          record(role, reference, { size: normalizeSize(size) });
        });
      });
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-gallery-media-toggle]');
        if (toggle && this.root.contains(toggle)) {
          event.preventDefault();
          this.openViewer();
          return;
        }

        const viewer = event.target.closest('[data-gallery-viewer]');
        if (viewer && this.root.contains(viewer)) {
          const close = event.target.closest('[data-gallery-viewer-close]');
          const step = event.target.closest('[data-gallery-viewer-step]');
          const thumb = event.target.closest('[data-gallery-viewer-thumb]');
          if (close) this.closeViewer();
          else if (step) this.stepViewer(Number(step.dataset.galleryViewerStep));
          else if (thumb) this.showViewer(Number(thumb.dataset.galleryViewerThumb));
          return;
        }
        const step = event.target.closest('[data-gallery-step]');
        if (step && this.root.contains(step)) {
          event.preventDefault();
          this.step(Number(step.dataset.galleryStep));
          return;
        }

        const thumb = event.target.closest('[data-gallery-thumb]');
        if (thumb && this.root.contains(thumb)) {
          event.preventDefault();
          this.showMedia(normalizeMediaId(thumb.dataset.galleryThumb));
        }
      });

      const viewer = this.root.querySelector('[data-gallery-viewer]');
      if (viewer) {
        viewer.addEventListener('cancel', (event) => { event.preventDefault(); this.closeViewer(); });
        viewer.addEventListener('pointerdown', (event) => {
          if (event.target.closest('button')) return;
          viewer.dataset.swipeStart = `${event.clientX},${event.clientY}`;
        }, { passive: true });
        viewer.addEventListener('pointerup', (event) => {
          const start = (viewer.dataset.swipeStart || '').split(',').map(Number);
          delete viewer.dataset.swipeStart;
          if (start.length !== 2 || start.some(Number.isNaN)) return;
          const dx = event.clientX - start[0];
          const dy = event.clientY - start[1];
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.2) this.stepViewer(dx < 0 ? 1 : -1);
        }, { passive: true });
      }

      if (this.pilot) {
        this.root.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' || event.target.closest('button, a, video, model-viewer')) return;
          this.touchStart = { x: event.clientX, y: event.clientY };
        }, { passive: true });
        this.root.addEventListener('pointerup', (event) => {
          if (!this.touchStart) return;
          const dx = event.clientX - this.touchStart.x;
          const dy = event.clientY - this.touchStart.y;
          this.touchStart = null;
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.25) this.step(dx < 0 ? 1 : -1);
        }, { passive: true });
      }

      this.host.addEventListener('lirago:variant-change', (event) => {
        const detail = event.detail || {};
        if (detail.variant) {
          this.applyVariant(detail.variant, detail.changedOption || null, Boolean(detail.initial));
          if (this.root.querySelector('[data-gallery-viewer][open]')) this.closeViewer();
        }
      });
    }

    optionIndex(name) {
      const options = (this.product && this.product.options) || [];
      return options.findIndex((option) => String(option).toLowerCase().includes(name));
    }

    variantContext(variant) {
      const options = (this.product && this.product.options) || [];
      const colorIndex = this.optionIndex('color');
      const sizeIndex = this.optionIndex('size');
      return {
        color: colorIndex >= 0 ? String(variant.options[colorIndex] || '') : '',
        size: sizeIndex >= 0 ? normalizeSize(variant.options[sizeIndex]) : ''
      };
    }

    currentVariant() {
      const selected = [...this.host.querySelectorAll('[data-option-position]:checked')]
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((input) => input.value);
      if (!this.product || !this.product.variants) return null;
      return this.product.variants.find((variant) => variant.options.every((value, index) => value === selected[index]))
        || this.product.variants.find((variant) => variant.id === this.product.selected_or_first_available_variant_id)
        || null;
    }

    referenceId(reference) {
      return normalizeMediaId(reference && (reference.media_gid || reference.mediaId || reference.id || reference));
    }

    lookupBucket(collection, key) {
      if (!collection || !key) return {};
      if (collection[key]) return collection[key];
      const normalized = String(key).toLowerCase();
      const match = Object.entries(collection).find(([candidate]) => String(candidate).toLowerCase() === normalized);
      return match ? match[1] : {};
    }

    pushReference(ids, reference) {
      const id = this.referenceId(reference);
      if (id && this.slides.some((slide) => slide.dataset.mediaId === id) && !ids.includes(id)) ids.push(id);
    }

    resolveTargetIds(variant, changedOption) {
      if (!this.manifestActive || !variant) return [];
      const context = this.variantContext(variant);
      const colors = this.manifest.colors || {};
      const sizes = this.manifest.sizes || {};
      const selectedColor = this.lookupBucket(colors, context.color);
      const selectedSize = this.lookupBucket(sizes, context.size);
      const shared = this.manifest.shared || {};
      const ids = [];

      const pushRoles = (roles, source) => roles.forEach((role) => {
        if (source && source[role]) this.pushReference(ids, source[role]);
      });

      if (String(changedOption || '').toLowerCase().includes('size')) {
        // A verified size mapping is authoritative for a size-change event.
        pushRoles(ROLE_ORDER, selectedSize);
        // If no verified mapping exists, retain the current appropriate
        // gallery target instead of guessing from color or legacy order.
        return ids;
      }

      // Color changes and initial loads prioritize the selected color, then
      // safe shared media, then the selected size mapping. Configuration
      // changes preserve the current valid Color + Size when possible.
      pushRoles(['hero', 'clean', 'detail_material'], selectedColor);
      if (!changedOption) {
        pushRoles(ROLE_ORDER, selectedSize);
        pushRoles(['hero', 'clean', 'detail_material', 'color_overview', 'size_compare', 'human_scale', 'lifestyle_2', 'lifestyle_3', 'included'], shared);
      } else {
        pushRoles(['hero', 'clean', 'detail_material', 'color_overview', 'size_compare', 'human_scale', 'lifestyle_2', 'lifestyle_3', 'included'], shared);
        pushRoles(ROLE_ORDER, selectedSize);
      }
      return ids;
    }


    // Product-scoped Bamboo Leaf gallery. The old media_manifest refers to
    // retired IDs; use the actual variant hero and current Shopify media files.
    applyBambooVariant(variant, changedOption) {
      const variants = (this.product && this.product.variants) || [];
      if (!variant || !variants.length) return;
      const productOptions = (this.product && this.product.options) || [];
      const sizeIndex = productOptions.findIndex((name) => String(name).toLowerCase().includes('size'));
      const size = sizeIndex >= 0 ? normalizeSize(variant.options[sizeIndex]) : '';
      const isSet = this.host.dataset.productUrl.includes('set-of-2');
      const allSlides = this.slides;
      const identifier = (slide) => {
        const img = slide.querySelector('img');
        return ((img && (img.getAttribute('src') || img.alt)) || '').toLowerCase();
      };
      const heroId = normalizeMediaId(variant.featured_media && variant.featured_media.id);
      const chosen = [];
      const add = (slide) => {
        if (!slide) return;
        const id = normalizeMediaId(slide.dataset.mediaId);
        if (id && !chosen.includes(id)) chosen.push(id);
      };
      add(allSlides.find((slide) => normalizeMediaId(slide.dataset.mediaId) === heroId));
      const dimension = isSet ? 'dimensions-' + size : 'dimensions-single';
      // The first 47x72 Set-of-2 dimension asset accidentally depicted one screen.
      // Prefer the verified two-screen infographic using its Shopify media ID.
      // Never fall back to the misleading one-screen image for this size.
      const corrected47SetMediaId = '46642839748687';
      const sizeSlide = isSet && size === '47x72'
        ? allSlides.find((slide) => normalizeMediaId(slide.dataset.mediaId) === corrected47SetMediaId)
          || allSlides.find((slide) => identifier(slide).includes('set_of_2_dimensions_47x72_each'))
        : allSlides.find((slide) => identifier(slide).includes(dimension));
      add(sizeSlide);
      add(allSlides.find((slide) => identifier(slide).includes('whats-included')));
      add(allSlides.find((slide) => identifier(slide).includes('assembly-guide')));
      add(allSlides.find((slide) => identifier(slide).includes('product-details')));
      if (isSet) {
        ['deck-lifestyle', 'garden-lifestyle', 'evening-patio-lifestyle'].forEach((needle) =>
          add(allSlides.find((slide) => identifier(slide).includes(needle)))
        );
      } else {
        add(allSlides.find((slide) => identifier(slide).includes('single-screen-patio-lifestyle')));
      }
      if (!chosen.length) add(allSlides[0]);
      this.bambooSequence = chosen;
      this.refreshSlides();
      const thumbsContainer = this.root.querySelector('.product-gallery__thumbs');
      this.thumbs.forEach((button) => {
        const index = chosen.indexOf(normalizeMediaId(button.dataset.galleryThumb));
        button.hidden = index < 0;
        button.style.order = index < 0 ? '999' : String(index);
        if (index >= 0) button.setAttribute('aria-label', 'Product image ' + (index + 1) + ' of ' + chosen.length);
      });
      if (thumbsContainer) thumbsContainer.dataset.bambooGallery = 'true';
      const selected = String(changedOption || '').toLowerCase().includes('size') && sizeSlide
        ? normalizeMediaId(sizeSlide.dataset.mediaId)
        : chosen[0];
      this.showMedia(selected);
    }

    applyVariant(variant, changedOption, initial) {
      this.refreshSlides();
      if (this.bambooUx) {
        this.applyBambooVariant(variant, changedOption);
        return;
      }
      if (!this.manifestActive) {
        if (variant.featured_media && variant.featured_media.id) this.showMedia(normalizeMediaId(variant.featured_media.id));
        else this.syncControls(this.currentIndex());
        return;
      }

      const targetIds = this.resolveTargetIds(variant, changedOption);
      if (targetIds.length) this.showMedia(targetIds[0]);
      else this.syncControls(this.currentIndex());
      this.applyRoleFit();
    }

    applyRoleFit() {
      this.refreshSlides();
      this.slides.forEach((slide) => {
        const mediaId = normalizeMediaId(slide.dataset.mediaId);
        const roleInfo = this.mediaRoles.get(mediaId);
        const roles = roleInfo ? roleInfo.roles : [];
        const role = roles[0] || 'legacy';
        const fit = roles.some((candidate) => COVER_ROLES.has(candidate)) ? 'cover' : 'contain';
        slide.dataset.mediaRole = role;
        slide.dataset.mediaFit = fit;
        slide.querySelectorAll('img, video, model-viewer').forEach((media) => {
          media.dataset.mediaRole = role;
          media.dataset.mediaFit = fit;
        });
        const thumb = this.thumbs.find((button) => normalizeMediaId(button.dataset.galleryThumb) === mediaId);
        if (thumb) thumb.dataset.mediaFit = fit;
      });
    }

    currentIndex() {
      this.refreshSlides();
      const active = this.navigationSlides.findIndex((slide) => !slide.hidden);
      return active >= 0 ? active : 0;
    }

    syncControls(index) {
      this.refreshSlides();
      const current = Math.max(0, Math.min(index, Math.max(0, this.navigationSlides.length - 1)));
      const counter = this.root.querySelector('[data-gallery-current]');
      if (counter) counter.textContent = String(current + 1);
      const total = this.root.querySelector('[data-gallery-total]');
      if (total) total.textContent = String(this.navigationSlides.length);
      this.root.querySelectorAll('[data-gallery-step]').forEach((button) => {
        button.disabled = Number(button.dataset.galleryStep) < 0 ? current === 0 : current >= this.navigationSlides.length - 1;
      });
    }

    showMedia(mediaId) {
      const targetId = normalizeMediaId(mediaId);
      this.refreshSlides();
      const target = this.slides.find((slide) => normalizeMediaId(slide.dataset.mediaId) === targetId);
      if (!target) return;
      this.internalSync = true;
      this.slides.forEach((slide) => {
        slide.hidden = slide !== target;
        slide.classList.toggle('is-active', slide === target);
      });
      this.thumbs.forEach((button) => {
        button.setAttribute('aria-current', String(normalizeMediaId(button.dataset.galleryThumb) === targetId));
      });
      this.activeMediaId = targetId;
      const navIndex = this.navigationSlides.indexOf(target);
      this.syncControls(navIndex >= 0 ? navIndex : this.currentIndex());
      this.internalSync = false;
    }

    step(delta) {
      this.refreshSlides();
      if (this.navigationSlides.length < 2) return;
      const targetIndex = Math.max(0, Math.min(this.currentIndex() + delta, this.navigationSlides.length - 1));
      this.showMedia(this.navigationSlides[targetIndex].dataset.mediaId);
    }

    openViewer() {
      this.refreshSlides();
      this.viewerSlides = this.relevantViewerSlides();
      const activeMediaId = this.slides.find((slide) => !slide.hidden)?.dataset.mediaId;
      const active = this.viewerSlides.findIndex((slide) => slide.dataset.mediaId === activeMediaId);
      this.viewerIndex = active >= 0 ? active : 0;
      this.renderViewer();
      const viewer = this.root.querySelector('[data-gallery-viewer]');
      if (!viewer) return;
      if (typeof viewer.showModal === 'function') viewer.showModal();
      else viewer.setAttribute('open', '');
    }

    closeViewer() {
      const viewer = this.root.querySelector('[data-gallery-viewer]');
      if (!viewer) return;
      if (typeof viewer.close === 'function' && viewer.open) viewer.close();
      else viewer.removeAttribute('open');
    }

    stepViewer(delta) {
      this.viewerSlides = this.relevantViewerSlides();
      if (this.viewerSlides.length < 2) return;
      this.viewerIndex = Math.max(0, Math.min(this.viewerIndex + delta, this.viewerSlides.length - 1));
      this.renderViewer();
    }

    showViewer(index) {
      this.viewerSlides = this.relevantViewerSlides();
      this.viewerIndex = Math.max(0, Math.min(index, this.viewerSlides.length - 1));
      this.renderViewer();
    }

    relevantViewerSlides() {
      this.refreshSlides();
      if (this.bambooUx) return this.navigationSlides;
      if (!this.pilot) return this.slides;
      const variant = this.currentVariant();
      const context = variant ? this.variantContext(variant) : { color: '', size: '' };
      return this.slides.filter((slide) => {
        if (slide.dataset.galleryPrimary === 'true') return true;
        const info = this.mediaRoles.get(normalizeMediaId(slide.dataset.mediaId));
        if (!info) return false;
        return info.contexts.some((entry) => entry.shared || (entry.color && String(entry.color).toLowerCase() === String(context.color).toLowerCase()) || (entry.size && entry.size === context.size));
      });
    }

    renderViewer() {
      this.viewerSlides = this.relevantViewerSlides();
      const viewer = this.root.querySelector('[data-gallery-viewer]');
      if (!viewer || !this.viewerSlides.length) return;
      this.viewerIndex = Math.max(0, Math.min(this.viewerIndex, this.viewerSlides.length - 1));
      const slide = this.viewerSlides[this.viewerIndex];
      const source = slide.querySelector('img, video, model-viewer');
      const image = viewer.querySelector('[data-gallery-viewer-image]');
      if (image && source) {
        image.src = source.currentSrc || source.src || '';
        image.alt = source.alt || '';
        image.style.objectFit = slide.dataset.mediaFit === 'cover' ? 'cover' : 'contain';
        image.dataset.mediaRole = slide.dataset.mediaRole || 'legacy';
      }
      viewer.querySelector('[data-gallery-viewer-current]').textContent = String(this.viewerIndex + 1);
      viewer.querySelector('[data-gallery-viewer-total]').textContent = String(this.viewerSlides.length);
      const thumbs = viewer.querySelector('[data-gallery-viewer-thumbs]');
      if (thumbs && thumbs.childElementCount !== this.viewerSlides.length) {
        thumbs.replaceChildren(...this.viewerSlides.map((item, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'product-gallery__viewer-thumb';
          button.dataset.galleryViewerThumb = String(index);
          button.setAttribute('aria-label', `Media ${index + 1}`);
          const thumbSource = item.querySelector('img');
          if (thumbSource) { const thumb = document.createElement('img'); thumb.src = thumbSource.currentSrc || thumbSource.src || ''; thumb.alt = ''; button.appendChild(thumb); }
          thumbs.appendChild(button);
          return button;
        }));
      }
      thumbs?.querySelectorAll('[data-gallery-viewer-thumb]').forEach((button) => {
        button.setAttribute('aria-current', String(Number(button.dataset.galleryViewerThumb) === this.viewerIndex));
      });
    }
  }

  class UnifiedPrivacyGallery {
    constructor(root, host) {
      this.root = root;
      this.host = host;
      this.product = parseJson(host.querySelector('[data-product-json]'), null);
      this.allSlides = [...root.querySelectorAll('.product-gallery__slide[data-media-id]')];
      this.thumbs = [...root.querySelectorAll('[data-gallery-thumb]')];
      this.stage = root.querySelector('[data-unified-image-open]');
      this.fullscreen = root.querySelector('[data-unified-fullscreen]');
      this.fullImage = root.querySelector('[data-unified-fullscreen-image]');
      this.integratedPresentation = root.dataset.galleryPresentation === 'integrated-six-slot';
      this.pureScreenPresentation = root.dataset.galleryPresentation === 'pure-screen-six-slot';
      this.sixSlotPresentation = this.integratedPresentation || this.pureScreenPresentation;
      this.primarySlots = ['hero', 'clean', 'size_compare', 'lifestyle', 'detail_material', 'included'];
      this.additionalSequence = [];
      this.viewingAdditional = false;
      this.viewerImageRevision = 0;
      this.mediaConfig = String(parseJson(root.querySelector('[data-unified-media-config]'), '')).split(';').filter(Boolean).map(entry => {
        const [mediaId, role, color, sizes] = entry.split('~');
        return { mediaId, role, color: color === '*' ? null : color, sizes: sizes === '*' ? null : sizes.split(',') };
      });
      this.sequence = [];
      this.activeMediaId = '';
      this.activeRole = 'hero';
      this.imageRevision = 0;
      this.pointerStart = null;
      this.suppressZoom = false;
      this.setupReachableOptions();
      this.bindEvents();
      const options = this.host.selectedOptions();
      const initial = this.product.variants.find(variant => variant.options.every((value, index) => value === options[index]));
      this.defaultVariant = this.product.variants.find(variant => variant.available);
      if (initial) this.applyVariant(initial);
      this.host.updateAvailabilityMatrix();
    }

    setupReachableOptions() {
      const variants = this.product.variants.filter(variant => variant.available);
      if (variants.length < 2) return;
      const visited = new Set([variants[0].id]);
      const queue = [variants[0]];
      for (let index = 0; index < queue.length; index += 1) {
        const current = queue[index];
        variants.forEach(candidate => {
          if (visited.has(candidate.id)) return;
          const differences = candidate.options.reduce((total, value, position) => total + Number(value !== current.options[position]), 0);
          if (differences === 1) {
            visited.add(candidate.id);
            queue.push(candidate);
          }
        });
      }
      if (visited.size === variants.length) return;
      this.root.dataset.variantReachability = 'disconnected-recovery';
      const updateAvailability = this.host.updateAvailabilityMatrix.bind(this.host);
      this.host.updateAvailabilityMatrix = () => {
        updateAvailability();
        this.host.querySelectorAll('[data-option-position]').forEach(input => {
          const position = Number(input.dataset.optionPosition) - 1;
          if (!variants.some(variant => variant.options[position] === input.value)) return;
          const valueControl = input.closest('.variant-value');
          if (valueControl) {
            valueControl.hidden = false;
            valueControl.classList.remove('is-unavailable', 'is-sold-out');
            valueControl.setAttribute('aria-hidden', 'false');
            valueControl.setAttribute('aria-label', input.getAttribute('aria-label') || input.value);
          }
          input.disabled = false;
          input.setAttribute('aria-disabled', 'false');
        });
      };
    }

    context(variant) {
      const colorIndex = this.product.options.findIndex(option => String(option).toLowerCase().includes('color'));
      const sizeIndex = this.product.options.findIndex(option => String(option).toLowerCase().includes('size'));
      return { color: variant.options[colorIndex], size: normalizeSize(variant.options[sizeIndex]), colorIndex, sizeIndex };
    }

    resolveVariant(input) {
      const selected = this.host.selectedOptions();
      const changedIndex = Number(input.dataset.optionPosition) - 1;
      const variants = this.product.variants.filter(variant => variant.available);
      const exact = variants.find(variant => variant.options.every((value, index) => value === selected[index]));
      if (exact) return exact;
      const candidates = variants.filter(variant => variant.options[changedIndex] === input.value);
      const colorIndex = this.product.options.findIndex(option => String(option).toLowerCase().includes('color'));
      const preferred = candidates.find(variant => variant.options.every((value, index) =>
        index === changedIndex || value === (index === colorIndex ? 'Black' : this.defaultVariant?.options[index])
      ));
      return preferred || candidates[0] || this.variant;
    }

    bindEvents() {
      this.host.addEventListener('change', event => {
        const input = event.target.closest('[data-option-position]');
        if (!input || !this.host.contains(input)) return;
        event.stopImmediatePropagation();
        const variant = this.resolveVariant(input);
        if (!variant) return;
        this.host.querySelectorAll('[data-option-position]').forEach(option => {
          option.checked = option.value === variant.options[Number(option.dataset.optionPosition) - 1];
        });
        this.host.onOptionChange();
      }, true);

      this.host.addEventListener('lirago:variant-change', event => {
        if (event.detail && event.detail.variant) this.applyVariant(event.detail.variant);
      });

      this.fullscreen.addEventListener('close', () => {
        if (!this.viewingAdditional) return;
        this.viewingAdditional = false; ++this.viewerImageRevision;
        this.fullscreen.setAttribute('aria-busy', 'false');
        this.showMedia(this.activeMediaId);
      });

      this.root.addEventListener('click', event => {
        const close = event.target.closest('[data-unified-close]');
        if (close) { this.fullscreen.close(); return; }
        const additional = event.target.closest('[data-gallery-additional]');
        if (additional && this.sixSlotPresentation) { event.preventDefault(); this.openAdditional(); return; }
        const step = event.target.closest('[data-gallery-step], [data-unified-step]');
        if (step) { event.preventDefault(); this.step(Number(step.dataset.galleryStep || step.dataset.unifiedStep), step.hasAttribute('data-unified-step')); return; }
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (thumb) { event.preventDefault(); this.showMedia(thumb.dataset.galleryThumb); return; }
        if (event.target.closest('[data-unified-image-open]')) {
          if (this.suppressZoom) { this.suppressZoom = false; return; }
          this.openFullscreen();
        }
      });

      this.root.addEventListener('keydown', event => {
        if (event.target === this.stage && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault(); this.openFullscreen();
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); this.step(event.key === 'ArrowRight' ? 1 : -1, !!event.target.closest('[data-unified-fullscreen]'));
        }
      });

      [this.stage, this.root.querySelector('[data-unified-swipe]')].forEach(surface => {
        if (!surface) return;
        surface.addEventListener('pointerdown', event => {
          if (!event.isPrimary || event.button > 0) return;
          this.suppressZoom = false;
          this.pointerStart = { x: event.clientX, y: event.clientY, surface };
          surface.setPointerCapture(event.pointerId);
        });
        surface.addEventListener('pointerup', event => {
          const start = this.pointerStart;
          this.pointerStart = null;
          if (!start || start.surface !== surface) return;
          const dx = event.clientX - start.x;
          const dy = event.clientY - start.y;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.2) {
            this.suppressZoom = surface === this.stage;
            this.step(dx < 0 ? 1 : -1, surface !== this.stage);
          }
        });
        surface.addEventListener('pointercancel', () => { this.pointerStart = null; });
      });
    }

    primarySlot(media) {
      if (!this.sixSlotPresentation) return media.role;
      if (this.pureScreenPresentation && media.role === 'human_scale') return 'size_compare';
      return ['lifestyle_2', 'lifestyle_3', 'human_scale'].includes(media.role) ? 'lifestyle' : media.role;
    }

    applyVariant(variant) {
      const previousId = this.activeMediaId;
      const previousRole = this.activeRole;
      const previousSlot = this.activeSlot || previousRole;
      const previousAdditional = this.additionalSequence.find(media => media.mediaId === this.additionalMediaId);
      this.variant = variant;
      const context = this.context(variant);
      const summaryText = [variant.options[context.sizeIndex], variant.options[context.colorIndex]].filter(Boolean).join(' · ');
      this.host.querySelectorAll('[data-family-selector] .family-selector__context').forEach(summary => {
        summary.textContent = summaryText;
      });
      const roles = this.integratedPresentation
        ? ['hero', 'clean', 'size_compare', 'lifestyle_2', 'human_scale', 'lifestyle_3', 'detail_material', 'included', 'color_overview']
        : ['hero', 'clean', 'size_compare', 'human_scale', 'lifestyle_2', 'lifestyle_3', 'detail_material', 'included', 'color_overview'];
      const rank = media => media.color && media.sizes ? 0 : media.color ? 1 : media.sizes ? 2 : 3;
      const seenIds = new Set();
      const seenRoles = new Set();
      const compatibleMedia = this.mediaConfig
        .filter(media => (!media.color || media.color === context.color) && (!media.sizes || media.sizes.includes(context.size)))
        .filter(media => this.allSlides.some(slide => slide.dataset.mediaId === media.mediaId))
        .sort((a, b) => roles.indexOf(a.role) - roles.indexOf(b.role) || rank(a) - rank(b));
      const resolvedSequence = compatibleMedia.filter(media => {
          if (seenIds.has(media.mediaId) || seenRoles.has(media.role)) return false;
          seenIds.add(media.mediaId); seenRoles.add(media.role); return true;
        });
      this.sequence = this.sixSlotPresentation
        ? this.primarySlots.map(slot => resolvedSequence.find(media => this.primarySlot(media) === slot)).filter(Boolean)
        : resolvedSequence;
      const primaryIds = new Set(this.sequence.map(media => media.mediaId));
      const additionalIds = new Set();
      this.additionalSequence = this.sixSlotPresentation ? compatibleMedia.filter(media => {
        if (primaryIds.has(media.mediaId) || additionalIds.has(media.mediaId)) return false;
        additionalIds.add(media.mediaId); return true;
      }) : [];
      if (this.sixSlotPresentation) {
        const button = this.root.querySelector('[data-gallery-additional]');
        if (button) button.hidden = !this.additionalSequence.length;
        this.allSlides.forEach(slide => {
          slide.dataset.galleryLayer = primaryIds.has(slide.dataset.mediaId) ? 'primary'
            : additionalIds.has(slide.dataset.mediaId) ? 'secondary' : 'hidden-from-primary';
        });
      }
      this.sequence.forEach(media => {
        const slide = this.allSlides.find(item => item.dataset.mediaId === media.mediaId);
        slide.dataset.mediaRole = media.role;
        slide.dataset.mediaFit = 'contain';
        slide.querySelectorAll('img').forEach(image => { image.style.objectFit = 'contain'; image.draggable = false; });
        const thumb = this.thumbs.find(item => item.dataset.galleryThumb === media.mediaId);
        if (thumb) { thumb.dataset.mediaFit = 'contain'; thumb.parentNode.appendChild(thumb); }
      });
      this.thumbs.forEach(thumb => {
        thumb.hidden = !primaryIds.has(thumb.dataset.galleryThumb);
      });
      const empty = this.root.querySelector('[data-unified-empty]');
      if (empty) empty.hidden = this.sequence.length > 0;
      this.stage.setAttribute('aria-disabled', String(!this.sequence.length));
      this.stage.tabIndex = this.sequence.length ? 0 : -1;
      const target = this.sequence.find(media => media.mediaId === previousId)
        || this.sequence.find(media => this.primarySlot(media) === previousSlot)
        || this.sequence.find(media => media.role === 'hero')
        || this.sequence[0];
      if (target) this.showMedia(target.mediaId);
      else {
        ++this.imageRevision;
        this.allSlides.forEach(slide => { slide.hidden = true; });
        this.fullImage.style.visibility = 'hidden';
        this.activeMediaId = ''; this.activeRole = '';
        this.root.setAttribute('aria-busy', 'false');
        if (this.fullscreen.open) this.fullscreen.close();
        this.syncControls();
      }
      if (this.viewingAdditional) {
        const additional = this.additionalSequence.find(media => media.mediaId === previousAdditional?.mediaId)
          || this.additionalSequence.find(media => previousAdditional && this.primarySlot(media) === this.primarySlot(previousAdditional));
        if (additional) this.showAdditionalMedia(additional.mediaId);
        else {
          this.viewingAdditional = false; ++this.viewerImageRevision;
          if (target) this.showMedia(target.mediaId);
          this.syncControls();
        }
      }
    }

    showMedia(rawId) {
      const targetId = normalizeMediaId(rawId);
      const media = this.sequence.find(item => item.mediaId === targetId);
      if (!media) return;
      const target = this.allSlides.find(slide => slide.dataset.mediaId === targetId);
      const image = target.querySelector('img');
      const revision = ++this.imageRevision;
      this.allSlides.forEach(slide => {
        slide.hidden = slide !== target;
        slide.classList.toggle('is-active', slide === target);
      });
      image.loading = 'eager';
      image.style.visibility = 'hidden';
      if (!this.viewingAdditional) this.fullImage.style.visibility = 'hidden';
      this.activeMediaId = targetId;
      this.activeRole = media.role;
      this.activeSlot = this.primarySlot(media);
      this.root.setAttribute('aria-busy', 'true');
      this.syncControls();
      const incoming = new Image();
      const sourceUrl = image.src;
      incoming.onload = async () => {
        try { await incoming.decode(); await image.decode(); } catch (error) {}
        if (revision !== this.imageRevision || this.activeMediaId !== targetId) return;
        if (!this.viewingAdditional) {
          this.fullImage.src = sourceUrl;
          this.fullImage.alt = image.alt || this.product.title;
          this.fullImage.dataset.mediaId = targetId;
          this.fullImage.style.visibility = 'visible';
        }
        image.style.visibility = 'visible';
        this.root.setAttribute('aria-busy', 'false');
      };
      incoming.onerror = () => {
        if (revision === this.imageRevision) {
          this.root.dataset.unifiedImageError = targetId;
          this.root.setAttribute('aria-busy', 'false');
        }
      };
      incoming.src = sourceUrl;
    }

    syncControls() {
      const index = this.sequence.findIndex(media => media.mediaId === this.activeMediaId);
      const viewerSequence = this.viewingAdditional ? this.additionalSequence : this.sequence;
      const viewerIndex = this.viewingAdditional ? viewerSequence.findIndex(media => media.mediaId === this.additionalMediaId) : index;
      this.root.querySelectorAll('[data-gallery-current]').forEach(counter => { counter.textContent = String(index + 1); });
      this.root.querySelectorAll('[data-gallery-total]').forEach(counter => { counter.textContent = String(this.sequence.length); });
      this.root.querySelectorAll('[data-unified-current]').forEach(counter => { counter.textContent = String(viewerIndex + 1); });
      this.root.querySelectorAll('[data-unified-total]').forEach(counter => { counter.textContent = String(viewerSequence.length); });
      this.root.querySelectorAll('[data-unified-additional-context]').forEach(label => { label.hidden = !this.viewingAdditional; });
      this.root.querySelectorAll('[data-gallery-step], [data-unified-step]').forEach(button => {
        const delta = Number(button.dataset.galleryStep || button.dataset.unifiedStep);
        const inViewer = button.hasAttribute('data-unified-step');
        const current = inViewer ? viewerIndex : index;
        const length = inViewer ? viewerSequence.length : this.sequence.length;
        button.disabled = delta < 0 ? current <= 0 : current >= length - 1;
      });
      this.thumbs.forEach(thumb => thumb.setAttribute('aria-current', String(thumb.dataset.galleryThumb === this.activeMediaId)));
    }

    step(delta, inViewer = false) {
      if (inViewer && this.viewingAdditional) {
        const index = this.additionalSequence.findIndex(media => media.mediaId === this.additionalMediaId);
        const next = this.additionalSequence[Math.max(0, Math.min(index + delta, this.additionalSequence.length - 1))];
        if (next) this.showAdditionalMedia(next.mediaId);
        return;
      }
      const index = this.sequence.findIndex(media => media.mediaId === this.activeMediaId);
      const next = this.sequence[Math.max(0, Math.min(index + delta, this.sequence.length - 1))];
      if (next) this.showMedia(next.mediaId);
    }

    openFullscreen() {
      if (!this.sequence.length || this.fullscreen.open) return;
      if (this.integratedPresentation) {
        this.viewingAdditional = false; ++this.viewerImageRevision;
        this.showMedia(this.activeMediaId);
      }
      this.fullscreen.showModal();
    }

    openAdditional() {
      if (!this.additionalSequence.length || this.fullscreen.open) return;
      this.viewingAdditional = true;
      this.fullscreen.showModal();
      this.showAdditionalMedia(this.additionalSequence[0].mediaId);
    }

    showAdditionalMedia(rawId) {
      const media = this.additionalSequence.find(item => item.mediaId === normalizeMediaId(rawId));
      if (!media || !this.viewingAdditional) return;
      const image = this.allSlides.find(slide => slide.dataset.mediaId === media.mediaId)?.querySelector('img');
      if (!image) return;
      const revision = ++this.viewerImageRevision;
      this.additionalMediaId = media.mediaId;
      this.fullImage.style.visibility = 'hidden';
      this.fullscreen.setAttribute('aria-busy', 'true');
      this.syncControls();
      const incoming = new Image();
      incoming.onload = async () => {
        try { await incoming.decode(); } catch (error) {}
        if (revision !== this.viewerImageRevision || !this.viewingAdditional) return;
        this.fullImage.src = image.src;
        this.fullImage.alt = image.alt || this.product.title;
        this.fullImage.dataset.mediaId = media.mediaId;
        this.fullImage.style.visibility = 'visible';
        this.fullscreen.setAttribute('aria-busy', 'false');
      };
      incoming.onerror = () => {
        if (revision !== this.viewerImageRevision) return;
        this.root.dataset.unifiedImageError = media.mediaId;
        this.fullscreen.setAttribute('aria-busy', 'false');
      };
      incoming.src = image.src;
    }
  }


  function boot() {
    document.querySelectorAll('[data-product-gallery]').forEach((root) => {
      if (!root.__liragoGalleryState) {
        const host = root.closest('product-info');
        if (host) {
          const unified = root.hasAttribute('data-unified-gallery') && root.dataset.unifiedTheme === '149786787919';
          if (unified) {
            customElements.whenDefined('product-info').then(() => {
              if (!root.__liragoGalleryState && String(parseJson(host.querySelector('[data-product-json]'), {}).id) === root.dataset.unifiedGallery) {
                root.__liragoGalleryState = new UnifiedPrivacyGallery(root, host);
              }
            });
          } else root.__liragoGalleryState = new LiragoGalleryState(root, host);
        }
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
